import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Typography } from '@mui/material';
import MBFCumulativeProjectsChart from './charts/MBFCumulativeProjectsChart';
import MBFUserDistributionChart from './charts/MBFUserDistributionChart';
import MBFProjectTypesChart from './charts/MBFProjectTypesChart';
import MBFYearWiseChart from './charts/MBFYearWiseChart';
import MBFDepartmentProjectsChart from './charts/MBFDepartmentProjectsChart';
import MBFTrainingsQuizzesChart from './charts/MBFTrainingsQuizzesChart';

// Import shared components
import { ErrorDisplay, SampleDataBadge, FullscreenChartModal } from '../common';

// Import API
import MBFDashboardAPIs from '../../../utilities/api/dashboard/MBFDashboardAPIs';

// Import sample data utilities
import { isMBFDataEmpty, getMBFSampleData } from '../../../utilities/sampleData/mbfDashboardSampleData';

/**
 * MBF Projects Tab Component
 * Displays project-related charts for MBF Dashboard
 * 
 * @param {object} dateRange - Date range filter { startDate, endDate }
 * @param {string} selectedTL - Selected TL ID for filtering
 */
const MBFProjectsTab = ({ dateRange = {}, selectedTL = '' }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [maximizedChart, setMaximizedChart] = useState(null);

    // Fetch data when dateRange, selectedTL, or refreshTrigger changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await MBFDashboardAPIs.getProjects({
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    tlId: selectedTL,
                });
                setData(response);
            } catch (err) {
                console.error('Error fetching MBF projects data:', err);
                setError('Failed to load projects data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange.startDate, dateRange.endDate, selectedTL, refreshTrigger]);

    const handleRetry = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size={48} sx={{ color: '#007bff' }} />
                <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
                    Fetching projects data...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return <ErrorDisplay message={error} onRetry={handleRetry} />;
    }

    // Extract data from API response with sample data fallbacks
    const rawCumulativeProjects = data?.cumulative_projects || [];
    const isCumulativeEmpty = isMBFDataEmpty(rawCumulativeProjects, 'array');
    const cumulativeProjects = isCumulativeEmpty ? getMBFSampleData('cumulativeProjects') : rawCumulativeProjects;

    const rawTLDistribution = data?.tl_user_distribution || [];
    const isTLDistributionEmpty = isMBFDataEmpty(rawTLDistribution, 'array');
    const tlUserDistribution = isTLDistributionEmpty ? getMBFSampleData('tlUserDistribution') : rawTLDistribution;

    const rawTypesOfProjects = data?.types_of_projects || [];
    const isTypesEmpty = isMBFDataEmpty(rawTypesOfProjects, 'array');
    const typesOfProjects = isTypesEmpty ? getMBFSampleData('typesOfProjects') : rawTypesOfProjects;

    const rawYearDistribution = data?.year_wise_distribution || [];
    const isYearDistEmpty = isMBFDataEmpty(rawYearDistribution, 'array');
    const yearDistribution = isYearDistEmpty ? getMBFSampleData('yearDistribution') : rawYearDistribution;

    const rawDeptProjects = data?.department_wise_projects || [];
    const isDeptProjectsEmpty = isMBFDataEmpty(rawDeptProjects, 'array');
    const departmentProjects = isDeptProjectsEmpty ? getMBFSampleData('departmentProjects') : rawDeptProjects;

    const rawTrainingsQuizzes = data?.trainings_vs_quizzes || [];
    const isTrainingsEmpty = isMBFDataEmpty(rawTrainingsQuizzes, 'array');
    const trainingsVsQuizzes = isTrainingsEmpty ? getMBFSampleData('trainingsVsQuizzes') : rawTrainingsQuizzes;

    const cumulativeChartData = {
        total: cumulativeProjects.map(item => item.total_projects || 0),
        completed: cumulativeProjects.map(item => item.completed_projects || 0),
    };

    // Helper function to get chart title for fullscreen modal
    const getChartTitle = (chartType) => {
        switch (chartType) {
            case 'cumulativeProjects': return 'Cumulative Projects Across All TLs';
            case 'userDistribution': return 'Distribution of users across TLs';
            case 'projectTypes': return 'Types Of Projects';
            case 'yearWise': return 'Year-wise Distribution';
            case 'departmentProjects': return 'Department-wise Projects';
            case 'trainingsQuizzes': return 'Trainings Completed V/S Quizzes Solved';
            default: return '';
        }
    };

    // Helper function to render chart content in fullscreen modal
    const renderChartContent = (chartType) => {
        switch (chartType) {
            case 'cumulativeProjects':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFCumulativeProjectsChart data={cumulativeChartData} isSampleData={isCumulativeEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'userDistribution':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFUserDistributionChart data={tlUserDistribution} isSampleData={isTLDistributionEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'projectTypes':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFProjectTypesChart data={typesOfProjects} isSampleData={isTypesEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'yearWise':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFYearWiseChart data={yearDistribution} isSampleData={isYearDistEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'departmentProjects':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFDepartmentProjectsChart data={departmentProjects} isSampleData={isDeptProjectsEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'trainingsQuizzes':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFTrainingsQuizzesChart data={trainingsVsQuizzes} isSampleData={isTrainingsEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ py: 2 }}>
            {/* Fullscreen Chart Modal */}
            <FullscreenChartModal
                open={Boolean(maximizedChart)}
                onClose={() => setMaximizedChart(null)}
                title={getChartTitle(maximizedChart)}
            >
                {renderChartContent(maximizedChart)}
            </FullscreenChartModal>

            {/* Row 1: Cumulative Projects & Distribution across TLs */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <MBFCumulativeProjectsChart
                        data={cumulativeChartData}
                        isSampleData={isCumulativeEmpty}
                        onMaximize={() => setMaximizedChart('cumulativeProjects')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MBFUserDistributionChart
                        data={tlUserDistribution}
                        isSampleData={isTLDistributionEmpty}
                        onMaximize={() => setMaximizedChart('userDistribution')}
                    />
                </Grid>
            </Grid>

            {/* Row 2: Types of Projects & Year-wise Distribution */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <MBFProjectTypesChart
                        data={typesOfProjects}
                        isSampleData={isTypesEmpty}
                        onMaximize={() => setMaximizedChart('projectTypes')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MBFYearWiseChart
                        data={yearDistribution}
                        isSampleData={isYearDistEmpty}
                        onMaximize={() => setMaximizedChart('yearWise')}
                    />
                </Grid>
            </Grid>

            {/* Row 3: Department-wise Projects & Trainings V/S Quizzes */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <MBFDepartmentProjectsChart
                        data={departmentProjects}
                        isSampleData={isDeptProjectsEmpty}
                        onMaximize={() => setMaximizedChart('departmentProjects')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MBFTrainingsQuizzesChart
                        data={trainingsVsQuizzes}
                        isSampleData={isTrainingsEmpty}
                        onMaximize={() => setMaximizedChart('trainingsQuizzes')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default MBFProjectsTab;
